import { useState } from 'react';
import { api, type Scenario } from '../api';
import { Dialogo, Etichetta } from '../componenti/base';
import { formattaEuro } from '../formato';

/**
 * Gli scenari: «e se incassassimo a sessanta giorni invece che a trenta?».
 *
 * Uno scenario non è un piano alternativo ma **un pugno di ipotesi applicate al
 * calcolo**. I fatti restano uno solo — fatture, movimenti, F24 — e cambia il
 * modo di proiettarli: per questo passare da uno all'altro è immediato e non
 * c'è niente da tenere allineato.
 *
 * «Effettivo» è sempre in cima e non si può cancellare, perché non è una riga:
 * è il calcolo senza ipotesi. Avere un punto di partenza che non si può
 * perdere è ciò che rende sicuro giocare con gli altri.
 */
export function SceltaScenario({
  scenari,
  attivo,
  onSceglie,
  onGestisci,
}: {
  scenari: Scenario[];
  attivo: string | null;
  onSceglie: (id: string | null) => void;
  onGestisci: () => void;
}) {
  return (
    <div className="flex items-center gap-1.5">
      <select
        className="campo w-auto py-1.5 text-sm"
        value={attivo ?? ''}
        onChange={(e) => onSceglie(e.target.value || null)}
        title="Con quali ipotesi proiettare"
      >
        <option value="">Effettivo</option>
        {scenari.map((s) => (
          <option key={s.id} value={s.id}>
            {s.nome}
          </option>
        ))}
      </select>
      <button className="bottone-secondario py-1.5" onClick={onGestisci} title="Crea o modifica gli scenari">
        Scenari…
      </button>
    </div>
  );
}

/** Come si legge un'ipotesi in una riga, senza aprire la scheda. */
export function riassunto(s: Scenario): string {
  const pezzi: string[] = [];
  const g = (n: number, che: string) => (n > 0 ? `${che} +${n} gg` : `${che} ${n} gg`);
  if (s.giorniIncasso !== 0) pezzi.push(g(s.giorniIncasso, 'incassi'));
  if (s.giorniPagamento !== 0) pezzi.push(g(s.giorniPagamento, 'pagamenti'));
  if (Number(s.quotaRicavi) !== 100) pezzi.push(`ricavi di piano al ${Number(s.quotaRicavi)} %`);
  if (Number(s.quotaCosti) !== 100) pezzi.push(`costi di piano al ${Number(s.quotaCosti)} %`);
  if (Number(s.quotaInsoluto) !== 0) pezzi.push(`${Number(s.quotaInsoluto)} % di insoluto sullo scaduto`);
  // Uno scenario senza ipotesi è «Effettivo» con un altro nome: dirlo evita di
  // farlo scoprire confrontando due colonne identiche.
  return pezzi.length === 0 ? 'nessuna ipotesi: uguale all’effettivo' : pezzi.join(' · ');
}

export function GestioneScenari({
  scenari,
  onChiudi,
  onCambiato,
}: {
  scenari: Scenario[];
  onChiudi: () => void;
  onCambiato: (eliminatoId?: string) => void;
}) {
  const [modifica, setModifica] = useState<Scenario | 'nuovo' | null>(null);

  if (modifica) {
    return (
      <SchedaScenario
        scenario={modifica === 'nuovo' ? null : modifica}
        onChiudi={() => setModifica(null)}
        onSalvato={() => {
          setModifica(null);
          onCambiato();
        }}
      />
    );
  }

  return (
    <Dialogo aperto titolo="Scenari" onChiudi={onChiudi} larghezza="max-w-2xl">
      <p className="text-sm text-attenuato">
        Un’ipotesi applicata al calcolo, non un piano a parte: le fatture e i movimenti restano quelli veri, cambia
        come vengono proiettati. Per questo una fattura nuova entra in tutti gli scenari insieme.
      </p>

      <ul className="mt-4 divide-y divide-inchiostro-200 border-y border-inchiostro-200">
        <li className="flex items-center justify-between py-2.5">
          <span className="text-sm">
            <span className="font-medium">Effettivo</span>
            <span className="block text-xs text-inchiostro-500">I dati come sono. Non si modifica né si cancella.</span>
          </span>
          <Etichetta testo="di partenza" />
        </li>
        {scenari.map((s) => (
          <li key={s.id} className="flex items-start justify-between gap-3 py-2.5">
            <span className="text-sm">
              <span className="font-medium">{s.nome}</span>
              <span className="block text-xs text-inchiostro-500">{riassunto(s)}</span>
              {s.note && <span className="block text-xs text-inchiostro-500">{s.note}</span>}
            </span>
            <span className="flex shrink-0 gap-2">
              <button className="bottone-secondario py-1" onClick={() => setModifica(s)}>
                Modifica
              </button>
              <button
                className="bottone-pericolo py-1"
                onClick={async () => {
                  await api.del(`/api/scenari/${s.id}`);
                  onCambiato(s.id);
                }}
              >
                Elimina
              </button>
            </span>
          </li>
        ))}
      </ul>

      <div className="mt-4 flex justify-between">
        <button className="bottone-primario" onClick={() => setModifica('nuovo')}>
          Nuovo scenario
        </button>
        <button className="bottone-secondario" onClick={onChiudi}>
          Chiudi
        </button>
      </div>
    </Dialogo>
  );
}

function SchedaScenario({
  scenario,
  onChiudi,
  onSalvato,
}: {
  scenario: Scenario | null;
  onChiudi: () => void;
  onSalvato: () => void;
}) {
  const [v, setV] = useState({
    nome: scenario?.nome ?? '',
    note: scenario?.note ?? '',
    giorniIncasso: String(scenario?.giorniIncasso ?? 0),
    giorniPagamento: String(scenario?.giorniPagamento ?? 0),
    quotaRicavi: String(Number(scenario?.quotaRicavi ?? 100)),
    quotaCosti: String(Number(scenario?.quotaCosti ?? 100)),
    quotaInsoluto: String(Number(scenario?.quotaInsoluto ?? 0)),
  });
  const [salvo, setSalvo] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);

  const salva = async () => {
    setSalvo(true);
    setErrore(null);
    const corpo = {
      nome: v.nome.trim(),
      note: v.note.trim() || null,
      giorniIncasso: Number(v.giorniIncasso) || 0,
      giorniPagamento: Number(v.giorniPagamento) || 0,
      quotaRicavi: Number(v.quotaRicavi),
      quotaCosti: Number(v.quotaCosti),
      quotaInsoluto: Number(v.quotaInsoluto),
    };
    try {
      if (scenario) await api.put(`/api/scenari/${scenario.id}`, corpo);
      else await api.post('/api/scenari', corpo);
      onSalvato();
    } catch (e) {
      setErrore(e instanceof Error ? e.message : 'Non salvato.');
      setSalvo(false);
    }
  };

  const campo = (chiave: keyof typeof v, etichetta: string, suffisso: string, aiuto: string) => (
    <label className="block">
      <span className="etichetta">{etichetta}</span>
      <span className="flex items-center gap-2">
        <input
          className="campo num w-24 text-right"
          inputMode="numeric"
          value={v[chiave]}
          onChange={(e) => setV({ ...v, [chiave]: e.target.value.replace(/[^\d-]/g, '') })}
        />
        <span className="text-sm text-inchiostro-500">{suffisso}</span>
      </span>
      <span className="mt-1 block text-xs text-inchiostro-500">{aiuto}</span>
    </label>
  );

  return (
    <Dialogo
      aperto
      titolo={scenario ? `Scenario · ${scenario.nome}` : 'Nuovo scenario'}
      onChiudi={onChiudi}
      larghezza="max-w-2xl"
    >
      <label className="block">
        <span className="etichetta">Nome</span>
        <input
          className="campo"
          autoFocus
          placeholder="Incassi a 60 giorni"
          value={v.nome}
          onChange={(e) => setV({ ...v, nome: e.target.value })}
        />
      </label>

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        {campo('giorniIncasso', 'Incassi', 'giorni', 'Positivo = i clienti pagano più tardi. Peggiora la cassa.')}
        {campo('giorniPagamento', 'Pagamenti', 'giorni', 'Positivo = paghiamo più tardi. È la leva che hai in mano.')}
        {campo('quotaRicavi', 'Ricavi di piano', '%', 'Vale su budget e ricorrenti, non sulle fatture già emesse.')}
        {campo('quotaCosti', 'Costi di piano', '%', 'Idem: i costi già fatturati restano quelli.')}
      </div>

      <div className="mt-4">
        {campo(
          'quotaInsoluto',
          'Insoluto sullo scaduto',
          '%',
          'Quanta parte dei crediti già scaduti si dà per persa. Oggi nel previsionale ci stanno per intero, come se dovessero arrivare tutti.',
        )}
      </div>

      <label className="mt-4 block">
        <span className="etichetta">Note</span>
        <input
          className="campo"
          placeholder="A cosa serve questo scenario"
          value={v.note}
          onChange={(e) => setV({ ...v, note: e.target.value })}
        />
      </label>

      {errore && <p className="mt-3 text-sm text-allerta">{errore}</p>}
      <div className="mt-4 flex justify-end gap-2">
        <button className="bottone-secondario" onClick={onChiudi} disabled={salvo}>
          Annulla
        </button>
        <button className="bottone-primario" onClick={() => void salva()} disabled={salvo || !v.nome.trim()}>
          {salvo ? 'Salvo…' : 'Salva'}
        </button>
      </div>
    </Dialogo>
  );
}

/** Non usata qui, ma tenuta accanto al resto: formatta una cifra di confronto. */
export const differenza = (a: number, b: number) => (a === b ? '—' : formattaEuro(a - b));
